// Web Vitals Tracking for Performance Monitoring
// Tracks Core Web Vitals: CLS, FID, FCP, LCP, TTFB

import { onCLS, onFCP, onLCP, onTTFB, onINP } from 'web-vitals';
import type { Metric } from 'web-vitals';

type SendToAnalytics = (metric: Metric) => void;

/**
 * Default analytics sender - logs to console in development
 * In production, only sends if VITE_ANALYTICS_ENDPOINT is configured
 */
const defaultSendToAnalytics: SendToAnalytics = (metric) => {
  // In development, just log to console
  if (import.meta.env.DEV) {
    console.log('[Web Vitals]', metric.name, metric.value, metric.rating);
    return;
  }

  // In production, only send if analytics endpoint is configured
  const analyticsEndpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT;
  if (!analyticsEndpoint) {
    // No endpoint configured - skip sending (no console noise)
    return;
  }

  // Send to configured analytics endpoint
  const body = JSON.stringify({
    name: metric.name,
    value: metric.value,
    id: metric.id,
    rating: metric.rating,
    delta: metric.delta,
    navigationType: metric.navigationType,
    timestamp: Date.now(),
    url: window.location.href,
  });

  // Use sendBeacon for reliability (survives page unload)
  if (navigator.sendBeacon) {
    navigator.sendBeacon(analyticsEndpoint, body);
  } else {
    fetch(analyticsEndpoint, {
      body,
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
    }).catch(() => {
      // Silently fail - analytics should not break the app
    });
  }
};

/**
 * Report all Web Vitals metrics
 * Call this once in main.tsx after app renders
 *
 * @param sendToAnalytics - Optional custom analytics handler
 */
export function reportWebVitals(sendToAnalytics: SendToAnalytics = defaultSendToAnalytics): void {
  // Cumulative Layout Shift - measures visual stability
  onCLS(sendToAnalytics);

  // First Contentful Paint - time until first content renders
  onFCP(sendToAnalytics);

  // Largest Contentful Paint - time until largest content renders
  onLCP(sendToAnalytics);

  // Time to First Byte - server response time
  onTTFB(sendToAnalytics);

  // Interaction to Next Paint - replaces FID (First Input Delay)
  onINP(sendToAnalytics);
}

/**
 * Get performance rating thresholds
 * Based on Google's Core Web Vitals thresholds
 */
export const VITALS_THRESHOLDS = {
  CLS: { good: 0.1, needsImprovement: 0.25 },
  FCP: { good: 1800, needsImprovement: 3000 },
  LCP: { good: 2500, needsImprovement: 4000 },
  TTFB: { good: 800, needsImprovement: 1800 },
  INP: { good: 200, needsImprovement: 500 },
} as const;

export default reportWebVitals;
